import React, { useRef } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { classService } from '../../../services';
import type { Class, ClassCreateRequest, ClassUpdateRequest } from '../types';
import CreateClassModal from './CreateClassModal';

interface EditClassModalProps {
  /** Lớp đang sửa; null → popup đóng. */
  classItem: Class | null;
  onClose: () => void;
}

// Chỉ gửi các trường có trên form: BE lưu thẳng request, gửi cả object lớp sẽ ghi đè invites/metadata/id.
const toUpdatePayload = (form: ClassCreateRequest): ClassUpdateRequest => ({
  code: form.code,
  name: form.name,
  schoolId: form.schoolId,
  subject: form.subject,
  grade: form.grade,
  schoolYear: form.schoolYear,
  studentCount: form.studentCount,
  teacherCount: form.teacherCount,
  status: form.status,
  // Luôn gửi: bỏ qua khi rỗng thì đổi trường xong BE vẫn giữ giáo viên của trường cũ.
  teacherId: form.teacherId || null,
});

const EditClassModal: React.FC<EditClassModalProps> = ({ classItem, onClose }) => {
  const queryClient = useQueryClient();
  // Giữ lớp cuối cùng để lúc popup đang đóng (animation) không nháy về giao diện "Tạo lớp".
  const lastClassRef = useRef(classItem);
  if (classItem) lastClassRef.current = classItem;

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClassUpdateRequest }) =>
      classService.updateClass(id, data),
    // Trả promise để mutateAsync chờ refetch xong mới đóng popup: mở lại ngay sẽ thấy dữ liệu mới,
    // không lưu đè giá trị cũ. Tab Lớp trong chi tiết trường dùng key riêng 'classroom'.
    // `async` để trả Promise<void>: react-query 5.80 (bản trong lockfile) không nhận Promise<[...]>.
    onSuccess: async (_, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['class', id] }),
        queryClient.invalidateQueries({ queryKey: ['classes'] }),
        queryClient.invalidateQueries({ queryKey: ['classroom'] }),
      ]);
    },
  });

  const handleSubmit = async (form: ClassCreateRequest) => {
    if (!classItem) return;
    try {
      await updateMutation.mutateAsync({ id: classItem.id, data: toUpdatePayload(form) });
      toast.success(`Đã lưu thông tin lớp "${form.name}"`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi lưu thông tin lớp học');
      // Ném lại để popup không đóng, người dùng sửa tiếp được.
      throw error;
    }
  };

  return (
    <CreateClassModal
      isOpen={!!classItem}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={updateMutation.isPending}
      initialData={classItem ?? lastClassRef.current}
    />
  );
};

export default EditClassModal;
